import { toast, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { startCpatAssessment } from "features/Cpat/helpers"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useRef, useState } from "react"
import { browserName, browserVersion, osName } from "react-device-detect"
import { useParams } from "react-router-dom"
import {
  browserIsSupportedForProctoring,
  cpatDurationInMinutes,
  cpatQuestions,
} from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import FaceCapture from "./_components/FaceCapture"
import LeftPanel from "./_components/LeftPanel"
import ProctoredTimeline from "./_components/ProctoredTimeline"
import StartMonitoredSessionDialog from "./_components/StartMonitoredSessionDialog"
import SystemChecks from "./_components/SystemChecks"

interface Props {
  onProceed?: () => void
}

export enum ProctoringStep {
  SYSTEM_CHECK = 1,
  FACE_CAPTURE = 2,
  FINAL_DIALOG = 3,
}

const CPATProctoringInstructions = ({ onProceed }: Props) => {
  const { attemptId } = useParams()
  const { profile } = useGlobalContext()
  const { cpat } = useEnrollments()

  const [step, setStep] = useState<ProctoringStep>(ProctoringStep.SYSTEM_CHECK)

  const { data: assessmentData } = useQuery({
    queryKey: ["cpat-assessment", cpat.latestCPAT?.id],
    queryFn: () =>
      api.cpat.getAssessment({ params: { assessmentId: cpat.latestCPAT?.id } }),
    enabled: !!cpat.latestCPAT?.id,
  })

  // State for System Checks
  const [cameraPermission, setCameraPermission] = useState<
    "pending" | "granted" | "denied"
  >("pending")
  const browserSupported = browserIsSupportedForProctoring === "supported"

  const enumerateCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cameras = devices
        .filter(d => d.kind === "videoinput")
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 6)}`,
        }))

      trackingService.trackEvent(
        TrackingEvents.cpatProcInitCamerasList,
        { cameras_count: cameras.length, cameras: cameras.map(c => c.label) },
        { services: ["PostHog"] }
      )
    } catch {
      // silently fail — not critical
    }
  }

  const getInitialCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      })
      stream.getTracks().forEach(track => track.stop())
      setCameraPermission("granted")
      // Enumerate after permission is granted so labels are populated
      await enumerateCameras()
    } catch (error) {
      console.error("Camera permission denied", error)
      setCameraPermission("denied")
      toast.error("Camera permissions are required to proceed.")
    }
  }

  useEffect(() => {
    getInitialCameraPermission()
  }, [])

  useEffect(() => {
    const stepName =
      step === ProctoringStep.SYSTEM_CHECK
        ? "system_check"
        : step === ProctoringStep.FACE_CAPTURE
        ? "face_capture"
        : "final_dialog"

    trackingService.trackEvent(
      TrackingEvents.cpatProcInitStepViewed,
      { step: stepName },
      { services: ["PostHog"] }
    )
  }, [step])

  const handleSystemCheckProceed = () => {
    if (cameraPermission === "granted" && browserSupported) {
      setStep(ProctoringStep.FACE_CAPTURE)
    }
  }

  // State for Face Capture
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)
  const [isCameraLoading, setIsCameraLoading] = useState(false)

  useEffect(() => {
    let stream: MediaStream | null = null

    const startCamera = async () => {
      if (step === ProctoringStep.FACE_CAPTURE && !capturedImage) {
        setIsCameraLoading(true)
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          })
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (error) {
          console.error("Error accessing camera", error)
          toast.error("Could not access camera for face capture.")
        } finally {
          setIsCameraLoading(false)
        }
      }
    }

    startCamera()

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
    }
  }, [step, capturedImage])

  const handleFinalProceed = () => {
    trackingService.trackEvent(
      TrackingEvents.cpatProcInitSessionStarted,
      undefined,
      { services: ["PostHog"] }
    )
    if (onProceed) {
      onProceed()
    } else if (attemptId) {
      startCpatAssessment({
        attemptId: attemptId,
        isProctored: true,
      })
    } else {
      toast.error("Attempt ID not found")
    }
  }

  const handleCapture = async () => {
    if (!videoRef.current) return

    try {
      const canvas = document.createElement("canvas")
      canvas.width = videoRef.current.videoWidth
      canvas.height = videoRef.current.videoHeight
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      ctx.save()
      ctx.scale(-1, 1) // Flip horizontally because video is mirrored
      ctx.drawImage(
        videoRef.current,
        -canvas.width,
        0,
        canvas.width,
        canvas.height
      )
      ctx.restore()

      canvas.toBlob(blob => {
        if (blob) {
          setCapturedBlob(blob)
          setCapturedImage(URL.createObjectURL(blob))
        } else {
          toast.error("Failed to capture image.")
        }
      }, "image/png")
    } catch (error) {
      console.error("Capture error", error)
    }
  }

  const handleRetake = () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage)
    }
    setCapturedImage(null)
    setCapturedBlob(null)
  }

  const handleSubmit = async () => {
    if (!capturedBlob) return

    trackingService.trackEvent(
      TrackingEvents.cpatProcInitFaceSubmitClicked,
      undefined,
      { services: ["PostHog"] }
    )

    setIsCapturing(true)
    const formData = new FormData()
    formData.append("image", capturedBlob, "capture.png")

    try {
      const res = await api.cpat.validateImage({ data: formData })
      if (res.goodImage) {
        trackingService.trackEvent(
          TrackingEvents.cpatProcInitFaceSubmitSuccess,
          undefined,
          { services: ["PostHog"] }
        )
        setStep(ProctoringStep.FINAL_DIALOG)
      } else {
        trackingService.trackEvent(
          TrackingEvents.cpatProcInitFaceSubmitFailed,
          { reason: "face_not_good" },
          { services: ["PostHog"] }
        )
        toast.error(
          "Face validation failed. Please ensure your face is clearly visible and try again."
        )
        handleRetake()
      }
    } catch (error) {
      console.error("Image validation error", error)
      trackingService.trackEvent(
        TrackingEvents.cpatProcInitFaceSubmitFailed,
        { reason: "api_error" },
        { services: ["PostHog"] }
      )
      toast.error("Failed to validate image. Please try again.")
    } finally {
      setIsCapturing(false)
    }
  }

  const assessmentName =
    assessmentData?.assessment.name ||
    "Comprehensive Pedagogy Assessment of Teachers"

  return (
    <div className="flex h-dvh w-full flex-col bg-surface-50">
      {step === ProctoringStep.FINAL_DIALOG && (
        <StartMonitoredSessionDialog
          onProceed={handleFinalProceed}
          open={true}
          onOpenChange={() => {
            setStep(ProctoringStep.FACE_CAPTURE)
          }}
        />
      )}
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left Section */}
        <LeftPanel
          step={step}
          userFirstName={profile.user.firstName}
          assessmentName={assessmentName}
          cpatQuestions={cpatQuestions}
          cpatDurationInMinutes={cpatDurationInMinutes}
        />

        {/* Center Divider / Timeline */}
        <ProctoredTimeline step={step} />

        {/* Right Section */}
        <div className="flex flex-1 flex-col justify-center overflow-y-auto p-4 md:p-6 lg:p-12 xl:p-20">
          <div className="mx-auto w-full max-w-[480px]">
            {/* Mobile Header */}
            <div className="mb-6 lg:hidden">
              <Typography
                variant="smallBody"
                className="mb-1 block tracking-wider text-onSurface-500"
              >
                Hi {profile.user.firstName},
              </Typography>
              <Typography variant="title2" className="text-primary-800">
                {assessmentName}
              </Typography>
            </div>
            {step === ProctoringStep.SYSTEM_CHECK && (
              <SystemChecks
                browserSupported={browserSupported}
                cameraPermission={cameraPermission}
                handleSystemCheckProceed={handleSystemCheckProceed}
              />
            )}

            {step === ProctoringStep.FACE_CAPTURE && (
              <FaceCapture
                videoRef={videoRef}
                isCapturing={isCapturing}
                isCameraLoading={isCameraLoading}
                capturedImage={capturedImage}
                onCapture={handleCapture}
                onRetake={handleRetake}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CPATProctoringInstructions
